// Zod runtime schemas — mirror Pydantic shapes from
// ``api/app/api/v1/dashboard/schemas.py`` 1:1. When the API contract
// changes, ``fetchValidated()`` raises a clear error here instead of
// failing deep in a component.
//
// Keep field names + types exact. Optional/null handling matches what
// FastAPI emits (omitted fields → undefined; explicit nulls → null).

import { z } from 'zod';

export const KPITotalsSchema = z.object({
  budget: z.number(),
  backlog: z.number(),
  pipeline: z.number(),
  combined: z.number(),
  coverage: z.number(),
  // Weighted pipeline (the export value is already weighted, so this is
  // Σ value). Optional with default so a frontend pointed at an older API
  // still parses — the hero's EOY projection then degrades to backlog only.
  pipeline_weighted: z.number().optional().default(0),
  // Unweighted pipeline: Σ value ÷ probability (inverse formula).
  pipeline_full: z.number().optional().default(0),
});
export type KPITotals = z.infer<typeof KPITotalsSchema>;

export const QuarterTotalSchema = z.object({
  quarter: z.string(),
  budget: z.number(),
  backlog: z.number(),
  pipeline: z.number(),
  combined: z.number(),
  coverage: z.number(),
});
export type QuarterTotal = z.infer<typeof QuarterTotalSchema>;

export const MonthlyChartEntrySchema = z.object({
  month: z.string(),
  month_label: z.string(),
  total_budget: z.number(),
  total_backlog: z.number(),
  pipeline_existing: z.number(),
  pipeline_new: z.number(),
  total_combined: z.number(),
  by_product: z.record(z.number()).default({}),
  by_business_unit: z.record(z.number()).default({}),
});
export type MonthlyChartEntry = z.infer<typeof MonthlyChartEntrySchema>;

// Aligned with backend TopClient — {client, value, monday_status}.
export const TopClientSchema = z.object({
  client: z.string(),
  value: z.number(),
  monday_status: z.string().nullable().optional(),
});
export type TopClient = z.infer<typeof TopClientSchema>;

export const BudgetDeltaEntrySchema = z.object({
  client: z.string(),
  budget: z.number(),
  backlog: z.number(),
  delta: z.number(),
  is_churned: z.boolean().default(false),
});
export type BudgetDeltaEntry = z.infer<typeof BudgetDeltaEntrySchema>;

export const BudgetDeltaAnalysisSchema = z.object({
  positive: z.array(BudgetDeltaEntrySchema),
  negative: z.array(BudgetDeltaEntrySchema),
  churned: z.array(BudgetDeltaEntrySchema),
  sum_positive: z.number(),
  sum_negative: z.number(),
  sum_churned: z.number(),
  total_delta: z.number(),
});

export const NewBusinessClientSchema = z.object({
  client: z.string(),
  backlog_value: z.number(),
  project_count: z.number(),
  monday_status: z.string().nullable().optional(),
});

export const NewBusinessImpactSchema = z.object({
  clients: z.array(NewBusinessClientSchema),
  total_value: z.number(),
});

// --- Pulse digest (GET /api/v1/dashboard/pulse) ---------------------------

export const PulseStoryMonthSchema = z.object({
  month: z.string(),
  delta: z.number(),
});
export type PulseStoryMonth = z.infer<typeof PulseStoryMonthSchema>;

export const PulseStorySchema = z.object({
  client: z.string(),
  delta: z.number(),
  from_total: z.number(),
  to_total: z.number(),
  project_count: z.number(),
  direction: z.enum(['up', 'down', 'new', 'lost']),
  months: z.array(PulseStoryMonthSchema),
  // default(0): tolerate the frontend deploying ahead of the API rollout that
  // adds the per-client bucket split (added − removed + changed = delta).
  added: z.number().default(0),
  removed: z.number().default(0),
  changed: z.number().default(0),
});
export type PulseStory = z.infer<typeof PulseStorySchema>;

// Per-family digest (backlog or pipeline). ``null`` at the parent level when
// the tenant has fewer than 2 uploads of that type.
export const PulseFamilyDigestSchema = z.object({
  from_upload_id: z.string(),
  to_upload_id: z.string(),
  from_uploaded_at: z.string(),
  to_uploaded_at: z.string(),
  total_added: z.number(),
  total_removed: z.number(),
  // default(0): tolerate the frontend deploying ahead of the API rollout that
  // adds this field. Once both are live it's always present.
  total_changed: z.number().default(0),
  net_change: z.number(),
  moved_clients: z.number(),
  stories: z.array(PulseStorySchema),
  // default(0): same rollout tolerance — movement outside the top-5 stories,
  // so sum(stories.delta) + others_delta == net_change on screen.
  others_count: z.number().default(0),
  others_delta: z.number().default(0),
  // Client-grain split of net_change (new + lost + existing = net) — the
  // grain the cards use, so the headline equation matches them exactly.
  new_clients_count: z.number().default(0),
  new_clients_delta: z.number().default(0),
  lost_clients_count: z.number().default(0),
  lost_clients_delta: z.number().default(0),
  existing_clients_count: z.number().default(0),
  existing_clients_delta: z.number().default(0),
  // Clients whose line items shifted between months with no net change.
  rephased_clients: z.number().default(0),
});
export type PulseFamilyDigest = z.infer<typeof PulseFamilyDigestSchema>;

export const PulseAlertsSchema = z.object({
  cogs_mismatch: z.number(),
  // default(0): tolerate the frontend deploying ahead of the API rollout that
  // adds these fields. Once both are live they're always present.
  cogs_underestimated: z.number().default(0),
  pipeline_historical: z.number(),
  hr_underestimated: z.number(),
  // default(0): tolerate an API still rolling out without this field —
  // frontend (Vercel) and API (cluster) don't deploy atomically.
  low_margin: z.number().default(0),
  draft_aging: z.number().default(0),
  high_severity: z.number(),
});
export type PulseAlerts = z.infer<typeof PulseAlertsSchema>;

export const PulseDigestSchema = z.object({
  backlog: PulseFamilyDigestSchema.nullable(),
  pipeline: PulseFamilyDigestSchema.nullable(),
  alerts: PulseAlertsSchema,
});
export type PulseDigest = z.infer<typeof PulseDigestSchema>;

// Aligned 1:1 with backend ``DashboardSnapshot``. Anything missing here
// will throw "contract violation" loudly in dev.
export const DashboardSnapshotSchema = z.object({
  totals: KPITotalsSchema,
  quarters: z.array(QuarterTotalSchema),
  chart: z.array(MonthlyChartEntrySchema),
  top_backlog: z.array(TopClientSchema),
  // default([]): tolerate the frontend deploying ahead of the API rollout that
  // adds this field.
  top_pipeline: z.array(TopClientSchema).default([]),
  top_featured: z.array(TopClientSchema),
  featured_product_label: z.string().nullable(),
  budget_delta: BudgetDeltaAnalysisSchema,
  new_business: NewBusinessImpactSchema,
  // Nullable + default: the frontend may deploy ahead of the API adding it.
  concentration: z
    .object({ top3_share: z.number(), hhi: z.number(), clients: z.number() })
    .nullable()
    .default(null),
});
export type DashboardSnapshot = z.infer<typeof DashboardSnapshotSchema>;
